import {
  validateMediaUnitsSearch,
  type MediaUnitsSearch,
} from '../features/media-units/lib/validate-search';

/**
 * Catalog "return context" — carries the catalog's active filter/sort search
 * state through the detail and edit routes (and the app-shell sidebar nav
 * link) as a single opaque `catalogSearch` query param, so navigating away
 * from the catalog and back — whether via the in-page back-link or the
 * persistent "Katalog nośników" sidebar item — restores the exact filter
 * configuration the user came from instead of always resetting to
 * `/media-units` with no params.
 *
 * Lives in `src/lib/` (not `features/media-units/lib/`) because it is
 * consumed both by the media-units feature AND by the app-shell sidebar nav
 * (`components/app-shell.tsx`), mirroring `lib/filter-config.ts`'s existing
 * shared-filter carry-over pattern.
 *
 * The same `catalogSearch` param also carries the /map route's `viewBbox`
 * viewport, when the caller encodes it (see `map-pin-popup-card.tsx`'s
 * "Szczegóły" navigation). `viewBbox` is /map-only and not part of
 * `MediaUnitsSearch`, so `decodeCatalogSearch` (which validates against the
 * catalog's own schema) silently drops it — `decodeCatalogSearchViewBbox`
 * below is the dedicated extractor the app-shell sidebar's "Mapa" link uses
 * instead. CU-86cb1nq31.
 *
 * Traceability: CU-86cajbuug, CU-86cb1nq31.
 */

/**
 * Serializes a search state (the catalog's own, or the catalog's shared-filter
 * subset plus /map's `viewBbox`) for forwarding as a single opaque query param.
 * Accepts `object` rather than `MediaUnitsSearch` specifically so callers
 * outside the catalog (e.g. the /map pin popup) can also embed `viewBbox` --
 * `MediaUnitsSearch` has no index signature, so it isn't structurally
 * assignable to `Record<string, unknown>`.
 */
export function encodeCatalogSearch(search: object): string {
  return JSON.stringify(search);
}

/**
 * Parses a `catalogSearch` query param back into validated catalog search state.
 * Falls back to the catalog's own defaults (via `validateMediaUnitsSearch`) for
 * missing, malformed, or tampered values — never throws. Any embedded `viewBbox`
 * (see module doc comment) is silently dropped here since it isn't part of the
 * catalog's own schema — use `decodeCatalogSearchViewBbox` to read it.
 */
export function decodeCatalogSearch(raw: string | undefined): MediaUnitsSearch {
  if (raw == null) return validateMediaUnitsSearch({});
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return validateMediaUnitsSearch({});
    return validateMediaUnitsSearch(parsed as Record<string, unknown>);
  } catch {
    return validateMediaUnitsSearch({});
  }
}

/** Search-object key carrying the /map route's persisted viewport. */
export const VIEW_BBOX_KEY = 'viewBbox';

/**
 * Extracts a string `viewBbox` field from a plain search object, or undefined
 * when absent or not a string. Single source of truth for this extraction,
 * reused by `decodeCatalogSearchViewBbox` below (decoding a serialized
 * `catalogSearch`), the app-shell sidebar's "Mapa" link (reading the current
 * URL search directly), and the map pin popup's "Szczegóły" navigation
 * (reading the current /map search directly). CU-86cb1nq31.
 */
export function extractViewBbox(search: object): string | undefined {
  const value = (search as Record<string, unknown>)[VIEW_BBOX_KEY];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Extracts the /map-only `viewBbox` field from a `catalogSearch` param, if
 * present. Used by the app-shell sidebar's "Mapa" link to restore the map
 * viewport when returning from a unit-detail page reached via a map pin
 * popup's "Szczegóły" navigation — `decodeCatalogSearch` alone can't surface
 * it since `viewBbox` isn't part of the catalog's own schema. CU-86cb1nq31.
 */
export function decodeCatalogSearchViewBbox(raw: string | undefined): string | undefined {
  if (raw == null) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    return extractViewBbox(parsed);
  } catch {
    return undefined;
  }
}
